/**
 * Onboarding Portal — Plan Detail (Option B)
 *
 * Authenticated customer contact view of a single onboarding plan.
 * Contacts can: view progress, update tasks, add tasks, add documents,
 * and see customer-facing notes.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { onboardingPortalService, type SharedPlanView, type SharedTask } from '@/services/api/onboardingPortal';
import {
  ArrowLeft, CheckCircle, Clock, AlertTriangle, Loader2, ChevronDown,
  ChevronRight, Calendar, Target, UserCircle, X, Edit, MessageSquare,
  Plus, FileText, Upload, ExternalLink, Trash2,
} from 'lucide-react';

const taskStatusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-700', icon: <Clock className="h-3 w-3" /> },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: <Loader2 className="h-3 w-3" /> },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700', icon: <AlertTriangle className="h-3 w-3" /> },
  waiting_customer: { label: 'Waiting', color: 'bg-yellow-100 text-yellow-700', icon: <Clock className="h-3 w-3" /> },
  done: { label: 'Done', color: 'bg-green-100 text-green-700', icon: <CheckCircle className="h-3 w-3" /> },
  skipped: { label: 'Skipped', color: 'bg-gray-100 text-gray-500', icon: <X className="h-3 w-3" /> },
};

const healthColors: Record<string, string> = {
  green: 'bg-green-100 text-green-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  red: 'bg-red-100 text-red-700',
};

export default function OnboardingPortalPlanDetailPage() {
  const navigate = useNavigate();
  const { planId } = useParams<{ planId: string }>();
  const [data, setData] = useState<SharedPlanView | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<'tasks' | 'documents'>('tasks');

  // Task editing
  const [editingTask, setEditingTask] = useState<SharedTask | null>(null);
  const [taskForm, setTaskForm] = useState({ status: '', notes: '', name: '', description: '', due_date: '', blocker_reason: '' });
  const [saving, setSaving] = useState(false);

  // Add task
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ name: '', description: '', due_date: '', priority: 'medium' });

  // Documents
  const [documents, setDocuments] = useState<any[]>([]);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [newDoc, setNewDoc] = useState({ document_name: '', file_url: '', document_type: 'other', description: '' });
  const [editingDoc, setEditingDoc] = useState<any | null>(null);
  const [editDocForm, setEditDocForm] = useState({ document_name: '', file_url: '', document_type: 'other', description: '' });

  useEffect(() => {
    if (!onboardingPortalService.isLoggedIn()) {
      navigate('/onboarding-portal/login');
      return;
    }
    loadPlan();
  }, [planId, navigate]);

  const loadPlan = async () => {
    if (!planId) return;
    setLoading(true);
    try {
      const result = await onboardingPortalService.getPlanDetail(parseInt(planId));
      setData(result);
      if (result.tasks) {
        setExpandedPhases(new Set(result.tasks.map(t => t.phase_name || 'Unassigned')));
      }
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        onboardingPortalService.logout();
        navigate('/onboarding-portal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    if (!planId) return;
    try {
      const docs = await onboardingPortalService.listDocuments(parseInt(planId));
      setDocuments(docs);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (tab === 'documents' && planId) loadDocuments();
  }, [tab, planId]);

  const togglePhase = (name: string) => {
    setExpandedPhases(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const openTaskEditor = (task: SharedTask) => {
    setEditingTask(task);
    setTaskForm({
      status: task.status,
      notes: task.notes || '',
      name: task.name,
      description: task.description || '',
      due_date: task.due_date || '',
      blocker_reason: task.blocker_reason || '',
    });
  };

  const handleSaveTask = async () => {
    if (!editingTask || !planId) return;
    setSaving(true);
    try {
      const updates: Record<string, string> = {};
      if (taskForm.status !== editingTask.status) updates.status = taskForm.status;
      if (taskForm.notes !== (editingTask.notes || '')) updates.notes = taskForm.notes;
      if (taskForm.name !== editingTask.name) updates.name = taskForm.name;
      if (taskForm.description !== (editingTask.description || '')) updates.description = taskForm.description;
      if (taskForm.due_date !== (editingTask.due_date || '')) updates.due_date = taskForm.due_date;
      if (taskForm.blocker_reason !== (editingTask.blocker_reason || '')) updates.blocker_reason = taskForm.blocker_reason;
      if (Object.keys(updates).length > 0) {
        await onboardingPortalService.updateTask(parseInt(planId), editingTask.id, updates);
      }
      setEditingTask(null);
      loadPlan();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTask = async () => {
    if (!planId || !newTask.name) return;
    setSaving(true);
    try {
      await onboardingPortalService.addTask(parseInt(planId), {
        name: newTask.name,
        description: newTask.description || undefined,
        due_date: newTask.due_date || undefined,
        priority: newTask.priority,
      });
      setShowAddTask(false);
      setNewTask({ name: '', description: '', due_date: '', priority: 'medium' });
      loadPlan();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDocument = async () => {
    if (!planId || !newDoc.document_name || !newDoc.file_url) return;
    setSaving(true);
    try {
      await onboardingPortalService.addDocument(parseInt(planId), {
        document_name: newDoc.document_name,
        file_url: newDoc.file_url,
        document_type: newDoc.document_type,
        description: newDoc.description || undefined,
      });
      setShowAddDoc(false);
      setNewDoc({ document_name: '', file_url: '', document_type: 'other', description: '' });
      loadDocuments();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to add document');
    } finally {
      setSaving(false);
    }
  };

  const openEditDoc = (doc: any) => {
    setEditingDoc(doc);
    setEditDocForm({
      document_name: doc.document_name || '',
      file_url: doc.file_url || '',
      document_type: doc.document_type || 'other',
      description: doc.description || '',
    });
  };

  const handleSaveDocEdit = async () => {
    if (!planId || !editingDoc) return;
    if (!editDocForm.document_name.trim() || !editDocForm.file_url.trim()) return;
    setSaving(true);
    try {
      await onboardingPortalService.updateDocument(parseInt(planId), editingDoc.id, {
        document_name: editDocForm.document_name.trim(),
        file_url: editDocForm.file_url.trim(),
        document_type: editDocForm.document_type,
        description: editDocForm.description.trim() || undefined,
      });
      setEditingDoc(null);
      loadDocuments();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update document');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDoc = async (docId: string) => {
    if (!planId) return;
    if (!confirm('Remove this document?')) return;
    try {
      await onboardingPortalService.deleteDocument(parseInt(planId), docId);
      loadDocuments();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete document');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-6">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Plan Not Found</h2>
            <p className="text-gray-500 mb-4">You may not have access to this plan.</p>
            <Button onClick={() => navigate('/onboarding-portal/plans')}>Back to Plans</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canEdit = data.access_level !== 'view';

  // Group tasks by phase
  const phaseGroups: Record<string, SharedTask[]> = {};
  if (data.tasks) {
    for (const t of data.tasks) {
      const phaseName = t.phase_name || 'Unassigned';
      if (!phaseGroups[phaseName]) phaseGroups[phaseName] = [];
      phaseGroups[phaseName].push(t);
    }
  }

  const tabDefs = [
    { id: 'tasks' as const, label: 'Tasks', icon: <CheckCircle className="h-4 w-4" /> },
    { id: 'documents' as const, label: 'Documents', icon: <FileText className="h-4 w-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/onboarding-portal/plans')}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="font-semibold text-gray-900 dark:text-white">
                  {data.plan.name || 'Untitled Plan'}
                </h1>
                {data.plan.customer_org_name && (
                  <p className="text-xs text-gray-500">{data.plan.customer_org_name}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={healthColors[data.plan.health] || 'bg-gray-100'}>{data.plan.health}</Badge>
              <Badge variant="outline">{data.plan.status}</Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {data.progress && (
            <Card>
              <CardContent className="py-3 text-center">
                <p className="text-xs text-gray-400">Progress</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{data.progress.percent}%</p>
                <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${data.progress.percent}%` }} />
                </div>
                <p className="text-xs text-gray-400 mt-1">{data.progress.completed}/{data.progress.total} tasks</p>
              </CardContent>
            </Card>
          )}
          {data.plan.csm_name && (
            <Card>
              <CardContent className="py-3 text-center">
                <p className="text-xs text-gray-400">Your CSM</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <UserCircle className="h-4 w-4 text-gray-400" />
                  <p className="font-medium text-sm">{data.plan.csm_name}</p>
                </div>
                {data.plan.csm_email && <p className="text-xs text-gray-400 mt-1">{data.plan.csm_email}</p>}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="py-3 text-center">
              <p className="text-xs text-gray-400">Start Date</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                <Calendar className="h-4 w-4 text-gray-400" />
                <p className="font-medium text-sm">{data.plan.start_date ? new Date(data.plan.start_date).toLocaleDateString() : '—'}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-3 text-center">
              <p className="text-xs text-gray-400">Go-Live Target</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                <Target className="h-4 w-4 text-gray-400" />
                <p className="font-medium text-sm">{data.plan.target_go_live_date ? new Date(data.plan.target_go_live_date).toLocaleDateString() : '—'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
          {tabDefs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Tasks tab */}
        {tab === 'tasks' && (
          <>
            {/* Add task button */}
            {canEdit && (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setShowAddTask(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Task
                </Button>
              </div>
            )}

            {Object.entries(phaseGroups).map(([phaseName, phaseTasks]) => {
              const expanded = expandedPhases.has(phaseName);
              const done = phaseTasks.filter(t => t.status === 'done' || t.status === 'skipped').length;
              return (
                <Card key={phaseName}>
                  <button
                    onClick={() => togglePhase(phaseName)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-t-lg"
                  >
                    <div className="flex items-center gap-2">
                      {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                      <span className="font-medium">{phaseName}</span>
                      <Badge variant="outline" className="text-xs">{done}/{phaseTasks.length}</Badge>
                    </div>
                    <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-600 rounded-full transition-all"
                        style={{ width: `${phaseTasks.length ? Math.round(done / phaseTasks.length * 100) : 0}%` }}
                      />
                    </div>
                  </button>
                  {expanded && (
                    <CardContent className="pt-0 pb-3">
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {phaseTasks.map(task => {
                          const cfg = taskStatusConfig[task.status] || taskStatusConfig.not_started;
                          const overdue = task.due_date && task.status !== 'done' && task.status !== 'skipped'
                            && new Date(task.due_date) < new Date();
                          return (
                            <div key={task.id} className="flex items-center justify-between py-2.5 px-2 text-sm group">
                              <div className="flex items-center gap-2 min-w-0">
                                {cfg.icon}
                                <span className={task.status === 'done' ? 'line-through text-gray-400' : ''}>{task.name}</span>
                                {task.milestone_name && <span className="text-xs text-gray-400 hidden sm:inline">({task.milestone_name})</span>}
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                {task.owner_name && <span className="text-xs text-gray-400 hidden md:inline">{task.owner_name}</span>}
                                {task.due_date && (
                                  <span className={`text-xs ${overdue ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                                    {new Date(task.due_date).toLocaleDateString()}
                                  </span>
                                )}
                                <Badge className={`${cfg.color} text-xs`}>{cfg.label}</Badge>
                                {canEdit && (
                                  <Button
                                    variant="ghost" size="sm"
                                    className="h-7 px-1.5 text-blue-600 hover:text-blue-800"
                                    onClick={() => openTaskEditor(task)}
                                  >
                                    <Edit className="h-3.5 w-3.5 mr-0.5" />
                                    <span className="text-xs">Edit</span>
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}

            {/* Customer-facing notes */}
            {data.notes && data.notes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Notes from Your Team
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {data.notes.map(note => (
                    <div key={note.id} className="border-l-2 border-blue-200 pl-3">
                      {note.title && <p className="font-medium text-sm">{note.title}</p>}
                      <p className="text-sm text-gray-600">{note.body}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {note.author_name && `${note.author_name} · `}
                        {new Date(note.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Documents tab */}
        {tab === 'documents' && (
          <>
            {canEdit && (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setShowAddDoc(true)}>
                  <Upload className="h-4 w-4 mr-1" />
                  Add Document
                </Button>
              </div>
            )}

            {documents.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No documents yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {documents.map((doc: any) => (
                  <Card key={doc.id}>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                            <span className="text-sm font-medium truncate">{doc.document_name}</span>
                            <Badge variant="outline" className="text-xs">{doc.document_type}</Badge>
                          </div>
                          {doc.description && <p className="text-xs text-gray-500 ml-6">{doc.description}</p>}
                          <div className="flex gap-3 text-xs text-gray-400 ml-6 mt-1">
                            {doc.uploaded_by_name && <span>By {doc.uploaded_by_name}</span>}
                            <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                            {doc.task_name && <span>Task: {doc.task_name}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {doc.file_url && (
                            <Button variant="ghost" size="sm" asChild>
                              <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                          {canEdit && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => openEditDoc(doc)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteDoc(doc.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Task edit dialog */}
      {editingTask && (
        <Dialog open onOpenChange={() => setEditingTask(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Task Name</Label>
                <Input value={taskForm.name} onChange={e => setTaskForm(f => ({ ...f, name: e.target.value }))} />
              </div>

              <div>
                <Label>Status</Label>
                <Select value={taskForm.status} onValueChange={v => setTaskForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(taskStatusConfig).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={taskForm.description}
                  onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={taskForm.due_date}
                    onChange={e => setTaskForm(f => ({ ...f, due_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Blocker Reason</Label>
                  <Input
                    value={taskForm.blocker_reason}
                    onChange={e => setTaskForm(f => ({ ...f, blocker_reason: e.target.value }))}
                    placeholder="If blocked, why?"
                  />
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={taskForm.notes}
                  onChange={e => setTaskForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Add a note..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingTask(null)}>Cancel</Button>
              <Button onClick={handleSaveTask} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Add task dialog */}
      {showAddTask && (
        <Dialog open onOpenChange={() => setShowAddTask(false)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Supporting Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Task Name</Label>
                <Input
                  value={newTask.name}
                  onChange={e => setNewTask(t => ({ ...t, name: e.target.value }))}
                  placeholder="What needs to be done?"
                  autoFocus
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={newTask.description}
                  onChange={e => setNewTask(t => ({ ...t, description: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={newTask.due_date}
                    onChange={e => setNewTask(t => ({ ...t, due_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={newTask.priority} onValueChange={v => setNewTask(t => ({ ...t, priority: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddTask(false)}>Cancel</Button>
              <Button onClick={handleAddTask} disabled={saving || !newTask.name}>
                {saving ? 'Adding...' : 'Add Task'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Add document dialog */}
      {showAddDoc && (
        <Dialog open onOpenChange={() => setShowAddDoc(false)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Document Name</Label>
                <Input
                  value={newDoc.document_name}
                  onChange={e => setNewDoc(d => ({ ...d, document_name: e.target.value }))}
                  placeholder="e.g. Signed SOW"
                  autoFocus
                />
              </div>
              <div>
                <Label>File URL</Label>
                <Input
                  value={newDoc.file_url}
                  onChange={e => setNewDoc(d => ({ ...d, file_url: e.target.value }))}
                  placeholder="https://drive.google.com/..."
                />
                <p className="text-xs text-gray-400 mt-1">Paste a link to your document (Google Drive, Dropbox, etc.)</p>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={newDoc.document_type} onValueChange={v => setNewDoc(d => ({ ...d, document_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="sow">SOW</SelectItem>
                    <SelectItem value="config">Configuration</SelectItem>
                    <SelectItem value="screenshot">Screenshot</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="checklist">Checklist</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={newDoc.description}
                  onChange={e => setNewDoc(d => ({ ...d, description: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDoc(false)}>Cancel</Button>
              <Button onClick={handleAddDocument} disabled={saving || !newDoc.document_name || !newDoc.file_url}>
                {saving ? 'Adding...' : 'Add Document'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit document dialog */}
      {editingDoc && (
        <Dialog open onOpenChange={() => setEditingDoc(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Document Name</Label>
                <Input
                  value={editDocForm.document_name}
                  onChange={e => setEditDocForm(d => ({ ...d, document_name: e.target.value }))}
                  autoFocus
                />
              </div>
              <div>
                <Label>File URL</Label>
                <Input
                  value={editDocForm.file_url}
                  onChange={e => setEditDocForm(d => ({ ...d, file_url: e.target.value }))}
                  placeholder="https://drive.google.com/..."
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={editDocForm.document_type} onValueChange={v => setEditDocForm(d => ({ ...d, document_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="sow">SOW</SelectItem>
                    <SelectItem value="config">Configuration</SelectItem>
                    <SelectItem value="screenshot">Screenshot</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="checklist">Checklist</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={editDocForm.description}
                  onChange={e => setEditDocForm(d => ({ ...d, description: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingDoc(null)}>Cancel</Button>
              <Button onClick={handleSaveDocEdit} disabled={saving || !editDocForm.document_name.trim() || !editDocForm.file_url.trim()}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
